import React, { useState } from "react";

import {
  Card,
  Button,
  Modal,
  Form,
  Table,
  Row,
  Col,
} from "react-bootstrap";

import {
  RiAddLine,
  RiDeleteBin6Line,
  RiEdit2Line,
  RiFileList3Line,
  RiSearchLine,
} from "react-icons/ri";

import styles from "./ProductVariants.module.css";

const ATTRIBUTE_TYPES = {
  ONE: "ONE",
  TWO: "TWO",
};

const defaultVariant = {
  attribute1: "",
  value1: "",

  attribute2: "",
  value2: "",

  sku: "",
  stock: "",

  mrp: "",
  discount: "",
  price: "",
};

const ProductVariants = () => {
  const [attributeType, setAttributeType] =
    useState(ATTRIBUTE_TYPES.ONE);

  const [variants, setVariants] =
    useState([]);

  const [bulkText, setBulkText] =
    useState("");

  const [showLoadModal, setShowLoadModal] =
    useState(false);

  const [showVariantModal, setShowVariantModal] =
    useState(false);

  const [editingIndex, setEditingIndex] =
    useState(null);

  const [productCode, setProductCode] =
    useState("");

  const [formData, setFormData] =
    useState(defaultVariant);

  /* SKU */

  const generateSKU = (data) => {
    const first =
      data.value1
        ?.replace(/\s/g, "")
        ?.substring(0, 3)
        ?.toUpperCase() || "VAR";

    const second =
      attributeType ===
      ATTRIBUTE_TYPES.TWO
        ? data.value2
            ?.replace(/\s/g, "")
            ?.substring(0, 3)
            ?.toUpperCase() || "000"
        : Math.floor(
            100 + Math.random() * 900
          );

    return `${first}-${second}`;
  };

  /* Add */

  const handleAddNew = () => {
    setEditingIndex(null);

    setFormData(defaultVariant);

    setShowVariantModal(true);
  };

  /* Edit */

  const handleEdit = (item, index) => {
    setEditingIndex(index);

    setFormData(item);

    setShowVariantModal(true);
  };

  /* Save */

  const handleSaveVariant = () => {
    const updatedData = {
      ...formData,
      sku: generateSKU(formData),
    };

    if (editingIndex !== null) {
      const updated = [...variants];

      updated[editingIndex] =
        updatedData;

      setVariants(updated);
    } else {
      setVariants((prev) => [
        updatedData,
        ...prev,
      ]);
    }

    setShowVariantModal(false);

    setFormData(defaultVariant);
  };

  /* Delete */

  const removeRow = (index) => {
    const updated = [...variants];

    updated.splice(index, 1);

    setVariants(updated);
  };

  /* Modal Change */

  const handleModalChange = (
    field,
    value
  ) => {
    let updated = {
      ...formData,
      [field]: value,
    };

    /* Auto SKU */

    updated.sku =
      generateSKU(updated);

    /* MRP + Discount */

    if (
      field === "mrp" ||
      field === "discount"
    ) {
      const mrp =
        parseFloat(updated.mrp) || 0;

      const discount =
        parseFloat(updated.discount) || 0;

      if (mrp > 0) {
        updated.price = (
          mrp -
          (mrp * discount) / 100
        ).toFixed(2);
      }
    }

    /* Price => Discount */

    if (field === "price") {
      const mrp =
        parseFloat(updated.mrp) || 0;

      const price =
        parseFloat(updated.price) || 0;

      if (mrp > 0) {
        updated.discount = (
          ((mrp - price) / mrp) *
          100
        ).toFixed(2);
      }
    }

    setFormData(updated);
  };

  /* Table Input Change */

  const handleTableChange = (
    index,
    field,
    value
  ) => {
    const updated = [...variants];

    updated[index][field] = value;

    const row = updated[index];

    /* MRP + Discount */

    if (
      field === "mrp" ||
      field === "discount"
    ) {
      const mrp =
        parseFloat(row.mrp) || 0;

      const discount =
        parseFloat(row.discount) || 0;

      if (mrp > 0) {
        row.price = (
          mrp -
          (mrp * discount) / 100
        ).toFixed(2);
      }
    }

    /* Price => Discount */

    if (field === "price") {
      const mrp =
        parseFloat(row.mrp) || 0;

      const price =
        parseFloat(row.price) || 0;

      if (mrp > 0) {
        row.discount = (
          ((mrp - price) / mrp) *
          100
        ).toFixed(2);
      }
    }

    setVariants(updated);
  };

  /* Bulk */

  const handleBulkCreate = () => {
    if (!bulkText.trim()) return;

    const lines = bulkText
      .split("\n")
      .filter((line) => line.trim());

    const generated = lines.map((line) => {
      const parts = line
        .split("#")
        .map((item) => item.trim());

      if (
        attributeType ===
        ATTRIBUTE_TYPES.ONE
      ) {
        const item = {
          attribute1: parts[0] || "",
          value1: parts[1] || "",

          attribute2: "",
          value2: "",

          stock: parts[2] || "",

          mrp: parts[3] || "",
          discount: parts[4] || "",
          price: parts[5] || "",
        };

        return {
          ...item,
          sku: generateSKU(item),
        };
      }

      const item = {
        attribute1: parts[0] || "",
        value1: parts[1] || "",

        attribute2: parts[2] || "",
        value2: parts[3] || "",

        stock: parts[4] || "",

        mrp: parts[5] || "",
        discount: parts[6] || "",
        price: parts[7] || "",
      };

      return {
        ...item,
        sku: generateSKU(item),
      };
    });

    setVariants((prev) => [
      ...generated,
      ...prev,
    ]);

    setBulkText("");
  };

  return (
    <>
      {/* Header */}

      <div className={styles.topBar}>
        <div className={styles.topTitle}>
          Product Variants
        </div>

        <Button
          className={styles.addBtn}
          onClick={handleAddNew}
        >
          <RiAddLine size={13} />
          Add Variant
        </Button>
      </div>

      {/* Bulk */}

      <Card className={styles.bulkCard}>
        <Card.Body className={styles.bulkBody}>
          <div className={styles.bulkTop}>
            <div className={styles.bulkTitle}>
              <RiFileList3Line size={13} />
              Bulk Create
            </div>

            <div className={styles.bulkAction}>
              <Button
                size="sm"
                variant="light"
                className={styles.loadBtn}
                onClick={() =>
                  setShowLoadModal(true)
                }
              >
                <RiSearchLine size={12} />
                Load
              </Button>

              <Button
                size="sm"
                className={styles.generateBtn}
                onClick={handleBulkCreate}
              >
                Generate
              </Button>
            </div>
          </div>

          {/* Type */}

          <div className={styles.attributeType}>
            <button
              type="button"
              className={`${styles.typeBtn} ${
                attributeType ===
                ATTRIBUTE_TYPES.ONE
                  ? styles.activeType
                  : ""
              }`}
              onClick={() =>
                setAttributeType(
                  ATTRIBUTE_TYPES.ONE
                )
              }
            >
              One Attribute
            </button>

            <button
              type="button"
              className={`${styles.typeBtn} ${
                attributeType ===
                ATTRIBUTE_TYPES.TWO
                  ? styles.activeType
                  : ""
              }`}
              onClick={() =>
                setAttributeType(
                  ATTRIBUTE_TYPES.TWO
                )
              }
            >
              Two Attribute
            </button>
          </div>

          {/* Example */}

          <div className={styles.exampleText}>
            {attributeType ===
            ATTRIBUTE_TYPES.ONE ? (
              <>
                Material Type #
                Cotton Blend # 10 #
                1299 # 10 # 1169
              </>
            ) : (
              <>
                Material Type #
                Cotton Blend # Color #
                Navy Blue # 10 # 1299
                # 10 # 1169
              </>
            )}
          </div>

          {/* Textarea */}

          <Form.Control
            as="textarea"
            rows={3}
            value={bulkText}
            placeholder="Paste bulk variant data..."
            className={styles.bulkTextarea}
            onChange={(e) =>
              setBulkText(
                e.target.value
              )
            }
          />
        </Card.Body>
      </Card>

      {/* Table */}

      <Card className={styles.tableCard}>
        <div className="table-responsive">
          <Table
            bordered
            hover
            className={styles.variantTable}
          >
            <thead>
              <tr>
                <th
                  className={
                    styles.indexCol
                  }
                >
                  #
                </th>

                <th
                  className={
                    styles.variantCol
                  }
                >
                  Variant
                </th>

                <th>SKU</th>

                <th>Stock</th>

                <th>MRP</th>

                <th>Disc%</th>

                <th>Price</th>

                <th
                  className={
                    styles.actionCol
                  }
                >
                  Act
                </th>
              </tr>
            </thead>

            <tbody>
              {variants.length > 0 ? (
                variants.map(
                  (item, index) => (
                    <tr key={index}>
                      <td>
                        {index + 1}
                      </td>

                      <td>
                        <div
                          className={
                            styles.variantName
                          }
                        >
                          <strong>
                            {item.value1}
                          </strong>

                          {attributeType ===
                            ATTRIBUTE_TYPES.TWO && (
                            <span>
                              /
                              {
                                item.value2
                              }
                            </span>
                          )}
                        </div>
                      </td>

                      <td>
                        {item.sku}
                      </td>

                      <td>
                        {item.stock}
                      </td>

                      {/* MRP */}

                      <td>
                        <input
                          type="number"
                          value={item.mrp}
                          className={
                            styles.tableInput
                          }
                          onChange={(e) =>
                            handleTableChange(
                              index,
                              "mrp",
                              e.target.value
                            )
                          }
                        />
                      </td>

                      {/* Discount */}

                      <td>
                        <input
                          type="number"
                          value={
                            item.discount
                          }
                          className={
                            styles.tableInput
                          }
                          onChange={(e) =>
                            handleTableChange(
                              index,
                              "discount",
                              e.target.value
                            )
                          }
                        />
                      </td>

                      {/* Price */}

                      <td>
                        <input
                          type="number"
                          value={item.price}
                          className={
                            styles.tableInput
                          }
                          onChange={(e) =>
                            handleTableChange(
                              index,
                              "price",
                              e.target.value
                            )
                          }
                        />
                      </td>

                      {/* Action */}

                      <td>
                        <div
                          className={
                            styles.actionWrap
                          }
                        >
                          <button
                            className={
                              styles.editBtn
                            }
                            onClick={() =>
                              handleEdit(
                                item,
                                index
                              )
                            }
                          >
                            <RiEdit2Line
                              size={12}
                            />
                          </button>

                          <button
                            className={
                              styles.deleteBtn
                            }
                            onClick={() =>
                              removeRow(index)
                            }
                          >
                            <RiDeleteBin6Line
                              size={12}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                )
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className={
                      styles.emptyText
                    }
                  >
                    No variants added
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* Add/Edit Modal */}

      <Modal
        show={showVariantModal}
        onHide={() =>
          setShowVariantModal(false)
        }
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title
            className={styles.modalTitle}
          >
            {editingIndex !== null
              ? "Edit Variant"
              : "Add Variant"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          className={styles.modalBody}
        >
          <Row className="g-2">
            <Col md={6}>
              <Form.Label
                className={styles.label}
              >
                Attribute Name
              </Form.Label>

              <Form.Control
                size="sm"
                value={
                  formData.attribute1
                }
                onChange={(e) =>
                  handleModalChange(
                    "attribute1",
                    e.target.value
                  )
                }
              />
            </Col>

            <Col md={6}>
              <Form.Label
                className={styles.label}
              >
                Attribute Value
              </Form.Label>

              <Form.Control
                size="sm"
                value={formData.value1}
                onChange={(e) =>
                  handleModalChange(
                    "value1",
                    e.target.value
                  )
                }
              />
            </Col>

            {attributeType ===
              ATTRIBUTE_TYPES.TWO && (
              <>
                <Col md={6}>
                  <Form.Label
                    className={
                      styles.label
                    }
                  >
                    Attribute Name
                  </Form.Label>

                  <Form.Control
                    size="sm"
                    value={
                      formData.attribute2
                    }
                    onChange={(e) =>
                      handleModalChange(
                        "attribute2",
                        e.target.value
                      )
                    }
                  />
                </Col>

                <Col md={6}>
                  <Form.Label
                    className={
                      styles.label
                    }
                  >
                    Attribute Value
                  </Form.Label>

                  <Form.Control
                    size="sm"
                    value={
                      formData.value2
                    }
                    onChange={(e) =>
                      handleModalChange(
                        "value2",
                        e.target.value
                      )
                    }
                  />
                </Col>
              </>
            )}

            <Col md={4}>
              <Form.Label
                className={styles.label}
              >
                Stock
              </Form.Label>

              <Form.Control
                size="sm"
                type="number"
                value={formData.stock}
                onChange={(e) =>
                  handleModalChange(
                    "stock",
                    e.target.value
                  )
                }
              />
            </Col>

            <Col md={4}>
              <Form.Label
                className={styles.label}
              >
                MRP
              </Form.Label>

              <Form.Control
                size="sm"
                type="number"
                value={formData.mrp}
                onChange={(e) =>
                  handleModalChange(
                    "mrp",
                    e.target.value
                  )
                }
              />
            </Col>

            <Col md={4}>
              <Form.Label
                className={styles.label}
              >
                Discount %
              </Form.Label>

              <Form.Control
                size="sm"
                type="number"
                value={
                  formData.discount
                }
                onChange={(e) =>
                  handleModalChange(
                    "discount",
                    e.target.value
                  )
                }
              />
            </Col>

            <Col md={12}>
              <Form.Label
                className={styles.label}
              >
                Final Price
              </Form.Label>

              <Form.Control
                size="sm"
                type="number"
                value={formData.price}
                onChange={(e) =>
                  handleModalChange(
                    "price",
                    e.target.value
                  )
                }
              />
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer
          className={styles.modalFooter}
        >
          <Button
            size="sm"
            variant="light"
            onClick={() =>
              setShowVariantModal(false)
            }
          >
            Close
          </Button>

          <Button
            size="sm"
            onClick={handleSaveVariant}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Load Modal */}

      <Modal
        show={showLoadModal}
        onHide={() =>
          setShowLoadModal(false)
        }
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title
            className={styles.modalTitle}
          >
            Load Product Variants
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label
              className={styles.label}
            >
              Product Code
            </Form.Label>

            <Form.Control
              size="sm"
              type="text"
              value={productCode}
              placeholder="Enter product code"
              onChange={(e) =>
                setProductCode(
                  e.target.value
                )
              }
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer
          className={styles.modalFooter}
        >
          <Button
            size="sm"
            variant="light"
            onClick={() =>
              setShowLoadModal(false)
            }
          >
            Close
          </Button>

          <Button size="sm">
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProductVariants;